import { getIdToken } from '@pooflabs/web';
import { ArrowDownToLine, Loader2, Plus, Wallet } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import { Address, Time } from '@/lib/db-client';
import { setRentalDeposits } from '@/lib/collections/rentalDeposits';
import { formatSol } from '@/utils/format';

interface RentalBalancePanelProps {
  walletAddress: string;
  balanceLamports: number;
  balanceLoading: boolean;
  hasActiveRental: boolean;
}

interface WithdrawResponse {
  payoutId: string;
  amount: number;
}

/**
 * Renter's SOL escrow balance for the rental marketplace. Deposits are a
 * renter-signed onchain transfer (rentalDeposits) to the treasury, followed
 * by an authenticated backend call to credit the ledger. Withdrawals go
 * straight through the backend, which pays out from the treasury.
 */
export const RentalBalancePanel: React.FC<RentalBalancePanelProps> = ({
  walletAddress,
  balanceLamports,
  balanceLoading,
  hasActiveRental,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [amountSol, setAmountSol] = useState('');
  const [depositing, setDepositing] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  const handleDeposit = async () => {
    const sol = Number(amountSol);
    if (!Number.isFinite(sol) || sol <= 0) {
      toast.error('Enter a valid SOL amount.');
      return;
    }
    const lamports = Math.round(sol * 1_000_000_000);

    setDepositing(true);
    try {
      const depositId = crypto.randomUUID();
      const created = await setRentalDeposits(depositId, {
        renter: Address.publicKey(walletAddress),
        amount: lamports,
        credited: false,
        createdAt: Time.Now,
      });
      if (!created) {
        toast.error('Deposit transfer was rejected or failed. Please try again.');
        return;
      }

      const token = await getIdToken();
      if (!token) {
        toast.error('Please reconnect your wallet and try again.');
        return;
      }
      const authApi = createAuthenticatedApiClient(token, walletAddress);
      await authApi.post('/api/rentals/deposit-credit', { depositId });

      toast.success(`Added ${sol} SOL to your rental balance.`);
      setAmountSol('');
      setDialogOpen(false);
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error && err.message ? err.message : 'Could not add funds. Please try again.');
    } finally {
      setDepositing(false);
    }
  };

  const handleWithdraw = async () => {
    if (balanceLamports <= 0) {
      toast.error('No balance to withdraw.');
      return;
    }
    if (hasActiveRental) {
      toast.error('Stop your active rental before withdrawing.');
      return;
    }

    setWithdrawing(true);
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Please reconnect your wallet and try again.');
        return;
      }
      const authApi = createAuthenticatedApiClient(token, walletAddress);
      const result = await authApi.post<WithdrawResponse>('/api/rentals/withdraw', {});
      toast.success(`Withdrew ${formatSol(result.amount, 4)} back to your wallet.`);
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error && err.message ? err.message : 'Withdrawal failed. Please try again.');
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <div className="rounded-3xl border border-border bg-card p-5 shadow-sm relative overflow-hidden">
      <div
        className="absolute inset-x-0 top-0 h-1 opacity-80"
        style={{ background: 'linear-gradient(90deg, transparent, hsl(var(--primary)), transparent)' }}
      />
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
        <Wallet className="w-3 h-3" /> Rental balance
      </p>
      <p className="text-2xl font-extrabold tabular-nums tracking-tight mt-1.5">
        {balanceLoading ? '—' : formatSol(balanceLamports, 4)}
      </p>
      <p className="text-[11px] text-muted-foreground mt-1">Spent pay-as-you-go while you rent a GPU.</p>

      <div className="flex items-center gap-2 mt-4">
        <button
          onClick={() => setDialogOpen(true)}
          className="flex-1 rounded-full bg-[#00C805] text-white font-bold py-2.5 text-sm shadow-lg shadow-[#00C805]/25 hover:bg-[#00b004] transition-all duration-300 flex items-center justify-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" /> Add funds
        </button>
        <button
          onClick={handleWithdraw}
          disabled={withdrawing || balanceLamports <= 0 || hasActiveRental}
          className="flex-1 rounded-full border border-input font-semibold py-2.5 text-sm hover:bg-secondary transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
        >
          {withdrawing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowDownToLine className="w-3.5 h-3.5" />}
          Withdraw
        </button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add funds</DialogTitle>
            <DialogDescription>
              Deposit native SOL into your rental balance. It's a one-time onchain transfer to the treasury — you'll be
              billed by the second while renting.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-1">
            <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">
              Amount (SOL)
            </label>
            <input
              type="number"
              min="0"
              step="0.001"
              value={amountSol}
              onChange={(e) => setAmountSol(e.target.value)}
              placeholder="0.05"
              className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <button
            onClick={handleDeposit}
            disabled={depositing}
            className="mt-2 w-full rounded-full bg-[#00C805] text-white font-bold py-3 shadow-lg shadow-[#00C805]/25 hover:bg-[#00b004] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {depositing && <Loader2 className="w-4 h-4 animate-spin" />}
            {depositing ? 'Depositing…' : 'Deposit'}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RentalBalancePanel;
